<template>
  <div class="card-image-maker">
    <div class="container">
      <h2>{{ getCardTypeName() }}图片制作</h2>

      <div class="form-section">
        <div class="form-group">
          <label>1. {{ getCardTypeName() }}信息</label>
          <div class="card-info-form">
            <div class="input-group">
              <label>批次号</label>
              <input type="text" v-model="cardInfo.batchNumber" readonly>
            </div>
            <div class="input-group">
              <label>卡密</label>
              <input type="text" v-model="cardInfo.cardNumber" readonly>
            </div>
            <div class="input-group">
              <label>有效期类型</label>
              <select v-model="cardInfo.validityType">
                <option value="week">周卡</option>
                <option value="month">月卡</option>
                <option value="year">年卡</option>
                <option value="custom">自定义天数</option>
              </select>
            </div>
            <div class="input-group" v-if="cardInfo.validityType === 'custom'">
              <label>自定义天数</label>
              <input type="number" v-model="cardInfo.validDays" placeholder="请输入天数" min="1">
            </div>
            <div class="input-group">
              <label>网址</label>
              <input type="text" v-model="cardInfo.website" placeholder="请输入网址">
            </div>
          </div>
        </div>
        
        <div class="form-group">
          <label>2. 文字样式设置</label>
          <div class="font-settings">
            <div class="font-tabs">
              <div 
                v-for="textType in textTypes" 
                :key="textType.value"
                class="font-tab"
                :class="{ active: activeFontTab === textType.value }"
                @click="activeFontTab = textType.value"
              >
                {{ textType.label }}
              </div>
            </div>
            
            <div class="font-controls">
              <div class="input-group">
                <label>字体</label>
                <select v-model="fontSettings[activeFontTab].fontFamily">
                  <option value="'Microsoft YaHei', sans-serif">微软雅黑</option>
                  <option value="'SimHei', sans-serif">黑体</option>
                  <option value="'SimSun', serif">宋体</option>
                  <option value="'Arial', sans-serif">Arial</option>
                  <option value="'Times New Roman', serif">Times New Roman</option>
                </select>
              </div>
              
              <div class="input-group">
                <label>字体大小</label>
                <input type="number" v-model="fontSettings[activeFontTab].fontSize" min="8" max="72">
              </div>
              
              <div class="input-group input-group-inline">
                <label>字体样式</label>
                <div class="font-style-controls">
                  <button 
                    class="style-btn"
                    :class="{ active: fontSettings[activeFontTab].isBold }"
                    @click="fontSettings[activeFontTab].isBold = !fontSettings[activeFontTab].isBold"
                  >
                    B
                  </button>
                  <button 
                    class="style-btn"
                    :class="{ active: fontSettings[activeFontTab].isItalic }"
                    @click="fontSettings[activeFontTab].isItalic = !fontSettings[activeFontTab].isItalic"
                  >
                    I
                  </button>
                </div>
              </div>
              
              <!-- 添加字间距控制 -->
              <div class="input-group">
                <label>字间距</label>
                <input type="number" v-model="fontSettings[activeFontTab].letterSpacing" min="-5" max="20" step="0.5">
              </div>
              
              <!-- 添加文字颜色控制 -->
              <div class="input-group">
                <label>文字颜色</label>
                <input type="color" v-model="fontSettings[activeFontTab].color">
              </div>
              
              <!-- 添加描边颜色控制 -->
              <div class="input-group">
                <label>描边颜色</label>
                <input type="color" v-model="fontSettings[activeFontTab].strokeColor">
              </div>

              <!-- 添加描边宽度控制 -->
              <div class="input-group">
                <label>描边宽度</label>
                <input type="number" v-model="fontSettings[activeFontTab].strokeWidth" min="0" max="10" step="0.5">
              </div>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label>3. 分隔线设置</label>
          <div class="separator-settings">
            <div class="input-group">
              <label>分隔线颜色</label>
              <input type="color" v-model="separatorColor">
            </div>
            <div class="input-group">
              <label>分隔线宽度</label>
              <input type="number" v-model="separatorWidth" min="1" max="10" step="0.5">
            </div>
          </div>
        </div>

        <!-- 4. 选择背景图片（移到预览效果上方） -->
        <div class="form-group">
          <label>4. 选择背景图片</label>

          <!-- 模板选择区域 -->
          <div class="template-selection">
            <label>模板选择</label>
            <div class="templates">
              <div
                v-for="(template, index) in backgroundTemplates"
                :key="index"
                class="template-item"
                :class="{ active: cardBackgroundImage === template.url }"
                @click="selectTemplate(template)"
              >
                <img :src="template.url" alt="背景模板" class="template-img">
                <div class="template-color-indicator" :style="{ backgroundColor: getSchemeColor(template.colorScheme) }"></div>
              </div>
            </div>
          </div>

          <div class="image-upload-container" v-if="!cardBackgroundImage">
            <input type="file" id="cardImageUpload" accept="image/jpeg,image/png,image/gif,image/bmp" @change="handleImageUpload" style="display: none;">
            <label for="cardImageUpload" class="upload-btn">
              点击上传图片
            </label>
          </div>

          <!-- 更换/移除图片按钮 -->
          <div class="image-upload-container" v-if="cardBackgroundImage" style="display: flex; gap: 10px; align-items: flex-start; margin-top: 15px;">
            <input type="file" id="cardImageUploadPreview" accept="image/jpeg,image/png,image/gif,image/bmp" @change="handleImageUpload" style="display: none;">
            <label for="cardImageUploadPreview" class="upload-btn" style="height: 40px; line-height: 40px; padding: 0 20px; color: white;">
              上传模版图片
            </label>
            <button class="upload-btn" style="background-color: #ff4444; height: 40px; line-height: 40px; padding: 0 20px; margin: 0; border: none; color: white; font-weight: bold;" @click="removeBackgroundImage">
              移除模版图片
            </button>
          </div>
        </div>

        <div class="form-group">
          <label>5. 预览效果</label>
          <div class="preview-cross-layout">
            <!-- 上方：Logo设置(左) + 锁定纵向(右) -->
            <div class="lock-top" style="justify-content: space-between; align-items: center;">
              <div class="logo-selection" style="display: flex; gap: 10px; align-items: center; flex-wrap: wrap;">
                <span style="font-size: 14px; font-weight: 500;">Logo:</span>
                <input type="file" id="logoUpload" accept="image/png,image/jpeg,image/gif" @change="handleLogoUpload" style="display: none;">
                <div style="display: flex; gap: 8px; align-items: flex-start;">
                  <label for="logoUpload" class="upload-btn" style="padding: 0 12px; background: #409eff; color: white; border-radius: 4px; cursor: pointer; font-size: 13px; display: inline-flex; align-items: center; height: 32px; box-sizing: border-box; line-height: 32px;">
                    {{ logoImage ? '更换' : '选择' }}
                  </label>
                  <button v-if="logoImage" class="btn" style="background: #f56c6c; color: white; padding: 0 12px; border-radius: 4px; border: none; cursor: pointer; font-size: 13px; display: inline-flex; align-items: center; height: 32px; box-sizing: border-box; margin: 0; line-height: 32px;" @click="removeLogo">移除</button>
                </div>

                <div style="display: flex; gap: 8px; align-items: center;">
                  <label style="font-size: 13px;">蒙版:</label>
                  <select v-model="fontSettings.logo.maskShape" style="padding: 4px 8px; border-radius: 4px; border: 1px solid #dcdfe6; font-size: 13px;">
                    <option value="none">无</option>
                    <option value="circle">圆形</option>
                    <option value="square">方形</option>
                  </select>
                </div>

                <div style="display: flex; gap: 8px; align-items: center;">
                  <label style="font-size: 13px;">背景:</label>
                  <input type="color" v-model="fontSettings.logo.color" style="width: 30px; height: 26px; border: 1px solid #dcdfe6; border-radius: 4px; cursor: pointer; padding: 0;">
                  <button v-if="fontSettings.logo.color !== '#ffffff'" @click="fontSettings.logo.color = '#ffffff'" style="font-size: 12px; color: #909399; background: none; border: none; cursor: pointer;">重置</button>
                </div>

                <div style="display: flex; gap: 8px; align-items: center;" v-if="logoImage">
                  <label style="font-size: 13px;">图大小:</label>
                  <input type="range" v-model.number="fontSettings.logo.fontSize" min="20" max="200" style="width: 80px;">
                  <span style="font-size: 12px; color: #909399; min-width: 30px;">{{ fontSettings.logo.fontSize }}px</span>
                </div>

                <div style="display: flex; gap: 8px; align-items: center;" v-if="logoImage">
                  <label style="font-size: 13px;">底大小:</label>
                  <input type="range" v-model.number="fontSettings.logo.bgSize" min="20" max="200" style="width: 80px;">
                  <span style="font-size: 12px; color: #909399; min-width: 30px;">{{ fontSettings.logo.bgSize }}px</span>
                </div>
              </div>

              <!-- 上方右侧：锁定纵向 -->
              <button
                class="btn lock-btn"
                :class="{ active: lockVertical }"
                @click="lockVertical = !lockVertical; $emit('lock-changed')"
                style="margin-left: auto;"
              >
                {{ lockVertical ? '🔒' : '🔓' }} 锁定纵向
              </button>
            </div>
            <!-- 中间和右边 -->
            <div class="preview-middle-row">
              <div class="preview-section">
                <div class="preview-container" v-if="cardBackgroundImage">
                  <canvas ref="cardCanvas" class="card-canvas"></canvas>
                </div>
                <div class="no-preview" v-else>
                  请先上传背景图片
                </div>
              </div>
              <!-- 右边：锁定横向 -->
              <div class="lock-right">
                <button 
                  class="btn lock-btn lock-btn-vertical" 
                  :class="{ active: lockHorizontal }"
                  @click="lockHorizontal = !lockHorizontal; $emit('lock-changed')"
                >
                  <span class="lock-icon">{{ lockHorizontal ? '🔒' : '🔓' }}</span>
                  <span class="lock-text-vertical">锁定横向</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 样式保存和加载区域 -->
        <div class="form-group">
          <label>6. 样式管理</label>
          <div class="style-management">
            <div class="style-save">
              <div class="input-group">
                <label>样式名称</label>
                <input type="text" v-model="newStyleName" placeholder="请输入样式名称" maxlength="20">
              </div>
              <div style="display: flex; gap: 10px; margin-top: 10px;">
                <button class="btn save-style-btn" @click="saveStyle" :disabled="!newStyleName.trim()">
                  保存样式
                </button>
                <button class="btn" style="background-color: #ff9800; color: white;" @click="overwriteCurrentScheme">
                  覆盖当前模板
                </button>
                <button class="btn" style="background-color: #9c27b0; color: white;" @click="saveAllTemplates">
                  保存所有模板
                </button>
              </div>
            </div>

            <div class="style-list" v-if="savedStyles.length > 0">
              <label>已保存的样式</label>
              <div class="styles">
                <div
                  v-for="(style, index) in savedStyles"
                  :key="index"
                  class="style-item"
                  :class="{ active: isCurrentStyle(style) }"
                >
                  <span class="style-name">{{ style.name }}</span>
                  <div class="style-actions">
                    <button class="style-action-btn apply-btn" @click="applyStyle(style)">应用</button>
                    <button class="style-action-btn delete-btn" @click="deleteStyle(index)">删除</button>
                  </div>
                </div>
              </div>
            </div>
            <div class="no-styles" v-else>
              暂无保存的样式
            </div>
          </div>
        </div>

        <!-- 在action-buttons区域添加关闭按钮 -->
        <div class="action-buttons">
          <button class="btn generate-btn" @click="generateCardImage" :disabled="!cardBackgroundImage || !cardInfo.batchNumber || !cardInfo.cardNumber">
            生成{{ getCardTypeName() }}图片&复制到剪切板
          </button>
          <button class="btn clear-btn" @click="clearForm">
            清空重新开始
          </button>
          <!-- 切换显示点击区域按钮 -->
          <button class="btn" :style="{ backgroundColor: showHitAreas ? '#2196F3' : '#9E9E9E', color: 'white' }" @click="showHitAreas = !showHitAreas; if(!showHitAreas) selectedTextType = null; drawCardPreview()">
            {{ showHitAreas ? '隐藏' : '显示' }}选中区域
          </button>
          <!-- 添加底部关闭按钮 -->
          <button class="btn" style="background-color: #ff4444; color: white;" @click="$emit('close')">
            关闭窗口
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import cardImageMakerLogic from './cardImageMakerLogic';

export default {...cardImageMakerLogic,};
</script>

<style scoped>
.card-image-maker {
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 20px;
}

.container {
  max-width: 800px;
  margin: 0 auto;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 30px;
}
/* 模板选择样式 */
.template-selection {
  margin-bottom: 20px;
}

.template-selection label {
  display: block;
  font-weight: bold;
  margin-bottom: 10px;
  color: #555;
}

.templates {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.template-item {
  width: 80px;
  height: 54px;
  border: 2px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
}


.template-item.active {
  border-color: #4a67fb;
  box-shadow: 0 0 0 2px rgba(74, 103, 251, 0.3);
}

.template-item:hover {
  border-color: #4a67fb;
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.template-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.template-color-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.template-item {
  position: relative;
}

h2 {
  text-align: center;
  color: #333;
  margin-bottom: 30px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-weight: bold;
  margin-bottom: 10px;
  color: #555;
}

/* 模板选择样式 */
.template-selection {
  margin-bottom: 20px;
}

.template-selection label {
  display: block;
  font-weight: bold;
  margin-bottom: 10px;
  color: #555;
}

.templates {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.template-item {
  width: 80px;
  height: 54px;
  border: 2px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
}

.template-item:hover {
  border-color: #4a67fb;
  transform: scale(1.05);
}

.template-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 图片上传样式 */
.image-upload-container {
  margin-top: 10px;
}

.upload-btn {
  display: inline-block;
  padding: 12px 24px;
  background-color: #4a67fb;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 15px;
  font-size: 16px;
  transition: background-color 0.3s ease;
}

.upload-btn:hover {
  background-color: #66b1ff;
}

.image-preview {
  position: relative;
  display: inline-block;
  margin-top: 10px;
}

.image-preview img {
  max-width: 100%;
  max-height: 250px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.remove-image-btn {
  position: absolute;
  top: -12px;
  right: -12px;
  background-color: #ff4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: background-color 0.3s ease;
}

.remove-image-btn:hover {
  background-color: #ff6666;
}

/* 表单样式 - 两列布局 */
.card-info-form {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.input-group label {
  font-weight: normal;
  color: #666;
  font-size: 14px;
}

.input-group input,
.input-group select {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  height: 40px; /* 增加明确的高度设置 */
  line-height: 20px; /* 设置合适的行高 */
  box-sizing: border-box; /* 确保内边距和边框包含在高度内 */
}

.input-group input:focus,
.input-group select:focus {
  outline: none;
  border-color: #4a67fb;
  box-shadow: 0 0 0 2px rgba(74, 103, 251, 0.2);
}

/* 字体设置样式 */
.font-settings {
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.font-tabs {
  display: flex;
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
}

.font-tab {
  padding: 10px 15px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.3s ease;
  flex: 1;
  text-align: center;
}

.font-tab:hover {
  background-color: #e9e9e9;
}

.font-tab.active {
  background-color: #4a67fb;
  color: white;
}
/* 在组件的style标签中添加以下样式 */
.font-controls {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  margin-top: 15px;
}

.font-controls .input-group {
  margin-bottom: 0;
}

.font-controls .input-group label {
  margin-bottom: 5px;
  font-size: 14px;
}

.font-controls .input-group input,
.font-controls .input-group select {
  padding: 6px 8px;
  font-size: 14px;
}

.font-controls .input-group-inline {
  display: flex;
  align-items: center;
}

.font-controls .input-group-inline label {
  margin-right: 10px;
  white-space: nowrap;
}

.font-style-controls {
  display: flex;
  gap: 5px;
}

.style-btn {
  width: 30px;
  height: 30px;
  font-weight: bold;
  border: 1px solid #ddd;
  background-color: white;
  cursor: pointer;
  border-radius: 4px;
}

.style-btn.active {
  background-color: #4a67fb;
  color: white;
  border-color: #4a67fb;
}

/* 十字形布局 */
.preview-cross-layout {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* 上面：锁定纵向 - 与预览窗口居中对齐，无间隙 */
.lock-top {
  margin-bottom: 0;
  display: flex;
  justify-content: center;
  padding-right: 65px; /* 预留右边竖排按钮的宽度(50px) + 间距(15px) */
}

.lock-top .lock-btn {
  font-size: 14px;
  padding: 8px 12px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 中间行：预览窗口 + 右边锁定横向 */
.preview-middle-row {
  display: flex;
  align-items: center;
  gap: 15px;
  width: 100%;
}

/* 预览区域样式 */
.preview-section {
  flex: 1;
}

/* 右边：锁定横向 */
.lock-right {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
}

/* 竖排按钮样式 */
.lock-btn-vertical {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px;
  height: auto;
  min-height: 120px;
}

.lock-btn-vertical .lock-icon {
  font-size: 16px;
  margin-bottom: 8px;
}

.lock-btn-vertical .lock-text-vertical {
  writing-mode: vertical-rl;
  text-orientation: upright;
  font-size: 14px;
  letter-spacing: 2px;
}

.lock-btn {
  padding: 8px 12px;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.lock-btn:hover {
  background-color: #e9e9e9;
  border-color: #ccc;
}

.lock-btn.active {
  background-color: #4a67fb;
  color: white;
  border-color: #4a67fb;
}

.lock-btn.active:hover {
  background-color: #3a56e8;
}

.preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 10px;
  min-height: 280px;
}

.card-canvas {
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  max-width: 100%;
  height: auto;
}

.no-preview {
  text-align: center;
  padding: 60px 0;
  color: #999;
  font-size: 18px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

/* 分隔线设置样式 */
.separator-settings {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 15px;
  background-color: #fafafa;
}

.separator-settings .input-group {
  margin-bottom: 0;
}

.separator-settings .input-group input[type="color"] {
  width: 60px;
  height: 40px;
  padding: 2px;
  cursor: pointer;
}

.separator-settings .input-group input[type="number"] {
  padding: 8px 10px;
}

/* 样式管理区域样式 */

/* 样式管理区域样式 */
.style-management {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 15px;
  background-color: #fafafa;
}

.style-save {
  display: flex;
  align-items: flex-end;
  gap: 15px;
  margin-bottom: 20px;
}

.style-save .input-group {
  flex: 1;
  margin-bottom: 0;
}

.save-style-btn {
  background-color: #4CAF50;
  color: white;
  padding: 10px 20px;
  font-size: 14px;
  min-width: 100px;
}

.save-style-btn:hover:not(:disabled) {
  background-color: #45a049;
}

.save-style-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.7;
}

.style-list label {
  display: block;
  font-weight: normal;
  color: #666;
  font-size: 14px;
  margin-bottom: 10px;
}

.styles {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
}

.style-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background-color: white;
  border: 2px solid #ddd;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.style-item.active {
  border-color: #4a67fb;
  background-color: rgba(74, 103, 251, 0.05);
  box-shadow: 0 0 0 2px rgba(74, 103, 251, 0.1);
}

.style-item:hover {
  border-color: #4a67fb;
  transform: translateX(2px);
}

.style-name {
  font-weight: 500;
  color: #333;
  font-size: 15px;
}

.style-actions {
  display: flex;
  gap: 8px;
}

.style-action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 3px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
}

.apply-btn {
  background-color: #4a67fb;
  color: white;
}

.apply-btn:hover {
  background-color: #66b1ff;
}

.delete-btn {
  background-color: #ff4444;
  color: white;
}

.delete-btn:hover {
  background-color: #ff6666;
}

.no-styles {
  text-align: center;
  padding: 40px 0;
  color: #999;
  font-size: 14px;
  background-color: white;
  border-radius: 4px;
}

/* 滚动条样式 */
.styles::-webkit-scrollbar {
  width: 8px;
}

.styles::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.styles::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.styles::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* 按钮样式 */
.action-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
}

.btn {
  padding: 12px 30px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: bold;
}

.generate-btn {
  background-color: #4a67fb;
  color: white;
}

.generate-btn:hover:not(:disabled) {
  background-color: #66b1ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(74, 103, 251, 0.3);
}

.generate-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.7;
}

.clear-btn {
  background-color: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
}

.clear-btn:hover {
  background-color: #e9e9e9;
  border-color: #ccc;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .container {
    padding: 20px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }

  .font-tabs {
    flex-direction: column;
  }

  .font-tab {
    text-align: left;
  }
  
  /* 小屏幕上改为垂直布局 */
  .preview-middle-row {
    flex-direction: column;
  }
  
  .lock-right {
    width: 100%;
  }
  
  .lock-right .lock-btn,
  .lock-top .lock-btn {
    width: 100%;
  }

  /* 小屏幕上会员卡信息恢复单列 */
  .card-info-form {
    grid-template-columns: 1fr;
  }

  /* 小屏幕上字体控制改为2列 */
  .font-controls {
    grid-template-columns: repeat(2, 1fr);
  }
}

</style>
